import intl from 'react-intl-universal'
import compareVersions from 'compare-versions'
import head from 'lodash/head'
import set from 'lodash/set'
import ArcUUID from '~/util/arc-identifiers'
import CONSTANTS from '~/constants'
import { get, cloneDeep } from 'lodash'
import { getCirculation } from '~/redux/storyData'
import { decode } from 'he'

/**
 * Recursive function to go through all layers of object and decode the values
 * @param {object} obj - encoded story template
 * @returns decoded object
 */
function decodeObject(obj) {
  for (const k in obj) {
    if (obj[k] instanceof Object) {
      decodeObject(obj[k])
    } else if (typeof obj[k] === 'string') {
      obj[k] = decode(obj[k])
    }
  }
  return obj
}

function decodeStoryTemplate(template) {
  // deep clone object to avoid mutating the original object
  const clonedTemplate = cloneDeep(template)
  return decodeObject(clonedTemplate)
}

const ANS_VERSION = '0.10.5'

function toArcSettingsFormat(template) {
  return {
    ...template,
    name: template.title,
    type: 'template',
    extra: template.data,
    data: undefined,
    title: undefined,
    siteId: template.data?.canonical_website,
  }
}

function fromArcSettingsFormat(template) {
  return {
    ...template,
    title: template.name,
    data: template.extra,
    extra: undefined,
    name: undefined,
  }
}

const getDefaultStory = (sourceSystem) => ({
  type: 'story',
  version: ANS_VERSION,
  headlines: {
    basic: '',
  },
  description: {
    basic: '',
  },
  // eslint-disable-next-line @typescript-eslint/camelcase
  content_elements: [
    {
      type: 'text',
      content: '',
    },
  ],
  source: {
    system: sourceSystem,
  },
})

// Default Template
const blankTemplate = (
  isCurrentDefaultVisible,
  sourceSystem,
  isCreateTemplate = false
) => [
  {
    id: 'default',
    default: false,
    visible:
      isCurrentDefaultVisible === undefined ? true : isCurrentDefaultVisible,
    title: isCreateTemplate ? '' : intl.get('storyTemplate.defaultTitle'),
    description: isCreateTemplate
      ? ''
      : intl.get('storyTemplate.defaultDescription', {
          appName: 'Composer',
        }),
    data: getDefaultStory(sourceSystem),
  },
]

// Add Default to templates list
function templatesProcessor(templates, sourceSystem) {
  // Checks current visible status for default template, applies to replacement template below
  let isCurrentDefaultVisible = true
  let defaultIdSaved = false

  if (Array.isArray(templates)) {
    const defaultTemplate = head(
      templates.filter((item) => item.id.toLowerCase() === 'default')
    )
    defaultIdSaved = !!defaultTemplate
    isCurrentDefaultVisible = defaultIdSaved ? defaultTemplate.visible : true
  }

  // check if the user accidently saved as id default and filter out
  // NOTE: this can be removed after switching to arc settings
  if (templates) {
    const tempCount = templates.length
    templates = templates.filter((item) => item.id.toLowerCase() !== 'default')
    // re-add base default
    if (tempCount !== templates.length) {
      templates.push(
        blankTemplate(isCurrentDefaultVisible, sourceSystem, true)[0]
      )
    }
  }
  // checks if we need to add in the initial template
  if (!templates || templates.length === 'undefined' || !templates.length) {
    templates = blankTemplate(isCurrentDefaultVisible, sourceSystem)
  } else if (!defaultIdSaved) {
    templates.push(blankTemplate(isCurrentDefaultVisible, sourceSystem)[0])
  }

  // Migrate source.system value of existing templates if feature flag is enabled
  templates = templates.map((template) => {
    // Defensive code for empty data object
    if (!template.data || !Object.keys(template.data).length) {
      template.data = getDefaultStory(sourceSystem)
    } else {
      set(template, 'data.source.system', sourceSystem)
    }
    return template
  })

  return templates
}

function storyTemplate(
  $q,
  $rootScope,
  settingsService,
  toastr,
  pharosService,
  $ngRedux
) {
  function getTemplatesFromArcSettings(id, sourceSystem) {
    let promise
    if (id === 'default') {
      promise = Promise.resolve(blankTemplate(true, sourceSystem))
    } else {
      const url =
        '/settings/api/v1/organization/document/v2' +
        (id ? `/${id}` : '?limit=0')
      promise = fetch(url, {
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) {
            throw response
          }
          return response
        })
        .then((response) => {
          return response.json()
        })
        .then((response) => {
          if (id) {
            const result = {
              ...response,
              extra: decodeStoryTemplate(response.extra),
            }
            return result
          } else {
            const result = response.rows.map((template) => {
              return {
                ...template,
                extra: decodeStoryTemplate(response.extra),
              }
            })
            return result
          }
        })
    }
    return Promise.all([promise, settingsService.fetchCompanySettings()])
      .then(([templates, resp]) => {
        $rootScope.globalSettings = resp.data
        return templates
      })
      .then((data) => {
        const arrayOfTemplates = Array.isArray(data) ? data : [data]
        return arrayOfTemplates.map(fromArcSettingsFormat)
      })
      .then((templates) => templatesProcessor(templates, sourceSystem))
      .then((processedTemplates) => {
        $rootScope.globalSettings.templates = processedTemplates
        $rootScope.template = processedTemplates.find(
          (template) => template.id === id
        )
        return processedTemplates
      })
      .catch(async (err) => {
        if (err instanceof Response) {
          console.log('Unable to load templates', await err.text())
        } else {
          console.log('Unable to load templates', err)
        }
        toastr.error(intl.get('storyTemplate.load.error'))
      })
  }

  // Fetch Template list
  function getTemplates(id) {
    return getTemplatesFromArcSettings(id, 'composer')
  }
  // Validate all template fields
  function validateTemplate(template) {
    if (!Object.keys(template.data).length) {
      const errorMessage = 'Template data is empty'
      pharosService.sendExceptionToSentry(new Error(errorMessage))
      return $q.reject([errorMessage])
    }

    // validate if template.title, template.description, and template.data.headlines.basic
    const templateInfo = {
      title: get(template, 'title', ''),
      description: get(template, 'description', ''),
    }
    const schema = {
      title: (value) => ''.concat(value).length > 0,
      description: (value) => ''.concat(value).length > 0,
    }
    const validate = (object, schema) =>
      Object.keys(schema)
        .filter((key) => !schema[key](object[key]))
        .map((key) => {
          return intl.get('storyDirective.validation.requireSaveFieldMissing', {
            field: intl.get(`storyDirective.templateForm.${key}`),
          })
        })
    const errorMessages = validate(templateInfo, schema)
    if (errorMessages.length > 0) {
      return $q.reject(errorMessages)
    }
    // This regex mirrors Arc settings' title validation requirements
    // https://github.com/WPMedia/arc-settings/blob/develop/src/validators.js#L20
    const regex = /^[0-9a-zA-ZÀ-ÖØ-öø-ỳ\u1100-\u11FF\u3040-\u30FF\u3130-\u318F\u4E00-\u9FEF\uA960-\uA97F\uAC00-\uD7A3\uD7B0-\uD7FF[\]()_~'";^\s.&@:,\\/|<>-]+$/
    if (!regex.test(templateInfo.title) || templateInfo.title.length < 2) {
      return $q.reject([intl.get('storyDirective.validation.invalidTitle')])
    }
    return $q.resolve()
  }
  // Return updated {Array} list of Templates
  function updateTemplateList(template) {
    return getTemplates(template.id).then((data) => {
      // Get index location of current template
      const index = data.findIndex((obj) => obj.id === template.id)
      const newTemplate = Object.assign({}, template)

      // Create a template unique ID if not set or default
      if (!newTemplate.id || newTemplate.id.toLowerCase() === 'default') {
        newTemplate.id = ArcUUID.create()
        newTemplate.visible = true
      }

      $rootScope.template.id = newTemplate.id
      if (index > -1 && newTemplate.id !== 'default') {
        data[index] = newTemplate
      } else {
        data.push(newTemplate)
      }
      return data
    })
  }

  function save(data, id) {
    const template = data.find((template) => template.id === id)
    if (!template) {
      throw Error('wrong template ID')
    }

    $rootScope.template = template

    window.localStorage.removeItem('template-operation')

    let promise
    if (template.createdAt) {
      promise = fetch(`/settings/api/v1/organization/document/${id}`, {
        credentials: 'same-origin',
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(toArcSettingsFormat(template)),
      })
      window.localStorage.setItem(
        'template-operation',
        JSON.stringify({
          type: 'update',
          name: template.title,
        })
      )
    } else {
      delete template.id
      promise = fetch(`/settings/api/v1/organization/document`, {
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(toArcSettingsFormat(template)),
      })
      window.localStorage.setItem(
        'template-operation',
        JSON.stringify({
          type: 'create',
          name: template.title,
        })
      )
    }
    return promise
      .then((response) => {
        if (!response.ok) {
          throw response
        }
        toastr.success(intl.get('storyTemplate.save.success'))
        return response
      })
      .then(async (response) => fromArcSettingsFormat(await response.json()))
      .then((template) => {
        const templateIdx = $rootScope.globalSettings.templates.findIndex(
          (template) => template.id === id
        )
        if (templateIdx >= 0) {
          $rootScope.globalSettings.templates[templateIdx] = template
        }
        $rootScope.template = template
        return template
      })
      .catch(async (errorOrResponse) => {
        window.localStorage.removeItem('template-operation')
        if (errorOrResponse instanceof Response) {
          throw await errorOrResponse.json()
        } else {
          throw errorOrResponse
        }
      })
  }

  // Return Promise with {Array} List of Templates
  this.list = async () => {
    const [data, settings] = await Promise.all([
      getTemplates(),
      settingsService.fetchCompanySettings(),
    ])

    const existingBlankTemplate = data.filter(
      (item) => item.id.toLowerCase() === 'default'
    )[0]

    // make sure blank template is added if desired
    if (!existingBlankTemplate && settings.data.defaultTemplateIdVisible) {
      const blankTemplateData = head(
        blankTemplate(settings.data.defaultTemplateIdVisible, 'composer')
      )
      return [
        ...data,
        {
          ...blankTemplateData,
          data: undefined,
          default: settings.data.defaultTemplateId === blankTemplateData.id,
        },
      ]
    }

    // hacky fix. if there is an existing blank template and it's not set to visible either in settings or on the template itself, delete
    // we're adding the blank in templateprocessor as part of the old flow
    if (
      !!existingBlankTemplate &&
      (settings.data.defaultTemplateIdVisible === false ||
        !existingBlankTemplate.visible)
    ) {
      const index = data.findIndex((i) => i.id === 'default')
      delete data[index]
      return [...data]
    }

    return [...data]
  }
  // (angular load template)
  // Returns Promise {Object} of Template with matching ID
  this.get = async (templateId) => {
    const data = await getTemplates(templateId)

    const template = data.filter(
      (item) => item.id.toLowerCase() === templateId.toLowerCase()
    )[0]

    // default to visible
    // make no sense expression: template.id === 'default' ? (template.visible = true) : ''
    // if we can't find the template, return the default
    if (!template) {
      console.log('can not find custom template, loading default...')
      return head(blankTemplate(true, 'composer'))
    } else if (!~compareVersions(template.data.version, ANS_VERSION)) {
      try {
        const response = await fetch(
          `${CONSTANTS.TRIDENT_ROUTE_COMPOSER_API}/v1/ans/upvert/${ANS_VERSION}`,
          {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(template.data),
          }
        )
        const story = await response.json()
        template.data = story
        return template
      } catch (error) {
        return template
      }
    } else {
      return template
    }
  }
  // Returns Promise {Array} after setting the default
  this.set = (id) => {
    return getTemplates(id).then((data) => {
      return data
        .map((item) => {
          // set if true if it isn't already true
          item.id === id && item.default === false
            ? (item.default = true)
            : (item.default = false)
          return item
        })
        .sort((a, b) => {
          // sort so default true is first in the list
          if (a.default === true && b.default === false) {
            return -1
          }
          if (a.default === false && b.default === true) {
            return 1
          }
          // if both are false, who cares
          return 0
        })
    })
  }
  // template visibility
  this.visible = () => {
    return getTemplates().then((data) => {
      if (data.every((i) => i.visible === false)) {
        const index = data.findIndex((i) => i.id === 'default')
        data[index].visible = true
        return data
      }
    })
  }
  // Returns Promise {Array} without matching ID
  // does not save settings, only save button should ever be responsible
  // for saving to dynamo. rootscope and settings ctrl scope get mismatched
  // easily and it screws with _version, keep all api posting in one place
  this.delete = (id) => {
    return getTemplates(id).then((templates) => {
      $rootScope.globalSettings = {
        ...$rootScope.globalSettings,
        templates: templates.filter((t) => t.id !== id),
      }
      return $rootScope.globalSettings.templates
    })
  }
  // (general save)
  // Returns Promise {Array} Adds Custom Template {Object} and Saves Settings
  this.save = () => {
    const deferred = $q.defer()
    const template = Object.assign({}, $rootScope.template)

    if (!template) {
      toastr.error(intl.get('storyTemplate.save.noTemplate'))
      return deferred.promise
    }

    // Update template with story info
    template.data = $rootScope.story
    template.data.circulations = getCirculation($ngRedux.getState())

    // Validate template
    return validateTemplate(template)
      .then(() => {
        // Update template list
        return updateTemplateList(template).then((data) => {
          return save(data, $rootScope.template.id).then((data) => {
            deferred.resolve(data.templates)
            return deferred.promise
          })
        })
      })
      .catch((error) => {
        // A hack to revert back generated id to default on save failure
        if (template.id === 'default') {
          $rootScope.template.id = 'default'
        }
        if (
          error.code === 'ERROR_DUPLICATE_ENTITY' ||
          error.context?.message === 'Validation error'
        ) {
          toastr.error(intl.get('storyTemplate.save.duplicateName'))
        } else if (error.code === 'ERROR_INVALID_REQUEST') {
          toastr.error(
            intl.get('storyTemplate.save.error', {
              msg: `${intl.get('storyDirective.validation.invalidTitle')}`,
            }),
            {
              allowHtml: true,
            }
          )
        } else {
          toastr.error(
            intl.get('storyTemplate.save.error', {
              msg: error.length ? error.join('<br />') : error.context?.message,
            }),
            {
              allowHtml: true,
            }
          )
        }
        $rootScope.isRequiredNotify = true
        throw error // halt exiting from create template page in workflowBar.controller.js
      })
  }

  $rootScope.$on('$locationChangeSuccess', () => {
    $rootScope.isRequiredNotify = false
  })
}

export default [
  '$q',
  '$rootScope',
  'settingsService',
  'toastr',
  'pharosService',
  '$ngRedux',
  storyTemplate,
]
